(function () {
  const ROOT_SELECTOR = '[data-swan-chat-pet]';
  const CHAT_SELECTOR = 'shopify-chat';
  const ACTIVATOR_SELECTOR = '[part="activator"]';
  const LAUNCHER_SELECTOR = '.swan-chat-pet__launcher';
  const GREETING_SELECTOR = '.swan-chat-pet__greeting';
  const GREETING_DELAY_MS = 450;
  const GREETING_DURATION_MS = 7000;
  const GREETING_TRANSITION_MS = 200;
  const GREETING_SESSION_KEY = 'ncb:pippa-greeting-shown:v1';
  const STICKY_BAR_SELECTOR = '.product-sticky-atc';
  const ACTIVATOR_DISCOVERY_MS = 100;
  const STABILIZATION_DELAYS_MS = [0, 100, 250, 500, 1000, 2000, 4000];
  const STICKY_BAR_ANIMATION_FALLBACK_MS = 180;

  function getActivator(chat) {
    return chat && chat.shadowRoot ? chat.shadowRoot.querySelector(ACTIVATOR_SELECTOR) : null;
  }

  function getStickyBarRect(documentRoot) {
    const bar = documentRoot ? documentRoot.querySelector(STICKY_BAR_SELECTOR) : null;
    if (!bar || bar.hidden) return null;

    const rect = bar.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return rect;
  }

  function parseDurationMs(value) {
    if (typeof value !== 'string') return null;

    const match = value.trim().match(/^(\d+(?:\.\d+)?)(m?s)$/i);
    if (!match) return null;

    const amount = Number.parseFloat(match[1]);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return match[2].toLowerCase() === 'ms' ? amount : amount * 1000;
  }

  // The bar slides up over `product-sticky-atc-in`, so the rect read the moment
  // it un-hides is up to 12px below where it settles. Read the real duration off
  // the bar rather than hardcoding it, so editing that keyframe cannot silently
  // leave Pippa perched short again.
  function getStickyBarSettleDelays(bar, view, fallbackMs = STICKY_BAR_ANIMATION_FALLBACK_MS) {
    let windowMs = fallbackMs;

    if (bar && view && typeof view.getComputedStyle === 'function') {
      try {
        const styles = view.getComputedStyle(bar);
        const declared = styles
          ? [styles.animationDuration, styles.transitionDuration]
              .map(parseDurationMs)
              .find((duration) => duration !== null)
          : null;
        if (declared) windowMs = declared;
      } catch (_error) {
        windowMs = fallbackMs;
      }
    }

    return [0, Math.round(windowMs / 2), Math.round(windowMs), Math.round(windowMs) + 100];
  }

  function calculatePetPosition(activatorRect, petWidth, petHeight, obstacleRect = null) {
    if (!activatorRect || !activatorRect.width || !activatorRect.height) return null;

    const snap = (value) => Math.round(value * 8) / 8;
    const left = snap(activatorRect.left + activatorRect.width - petWidth);
    let top = snap(activatorRect.bottom - petHeight);

    if (obstacleRect && obstacleRect.width && obstacleRect.height) {
      const overlapsHorizontally =
        left + petWidth > obstacleRect.left && left < obstacleRect.left + obstacleRect.width;
      const overlapsVertically =
        top + petHeight > obstacleRect.top && top < obstacleRect.top + obstacleRect.height;

      if (overlapsHorizontally && overlapsVertically) {
        top = snap(obstacleRect.top - petHeight);
      }
    }

    return { left, top };
  }

  function positionPet(root, activator, documentRoot) {
    const activatorRect = activator.getBoundingClientRect();
    const petRect = root.getBoundingClientRect();
    const position = calculatePetPosition(
      activatorRect,
      petRect.width,
      petRect.height,
      getStickyBarRect(documentRoot)
    );
    if (!position) return false;

    const transform = `translate3d(${position.left}px, ${position.top}px, 0)`;
    if (root.style.transform !== transform) {
      root.style.transform = transform;
    }
    return true;
  }

  function createFrameScheduler(callback, requestFrame, cancelFrame) {
    let frameId = null;

    const schedule = () => {
      if (frameId !== null) return;
      frameId = requestFrame(() => {
        frameId = null;
        callback();
      });
    };

    schedule.cancel = () => {
      if (frameId === null) return;
      cancelFrame(frameId);
      frameId = null;
    };

    return schedule;
  }

  function openChat(chat) {
    if (!chat) return false;
    chat.open = true;
    if (!chat.hasAttribute('open')) chat.setAttribute('open', '');
    return true;
  }

  function setReading(root, reading) {
    root.classList.toggle('is-reading', reading);
  }

  function syncUnread(root, activator) {
    const indicator = activator?.querySelector('.activator__indicator');
    const hasUnread = Boolean(indicator && indicator.classList.contains('activator__indicator--active'));
    const launcher = root.querySelector(LAUNCHER_SELECTOR);

    root.classList.toggle('has-unread', hasUnread);
    launcher?.setAttribute('aria-label', hasUnread ? 'Open chat with Pippa, unread message' : 'Open chat with Pippa');
  }

  function claimGreeting(storage) {
    if (!storage) return false;

    try {
      if (storage.getItem(GREETING_SESSION_KEY) === '1') return false;
      storage.setItem(GREETING_SESSION_KEY, '1');
      return true;
    } catch (_error) {
      return false;
    }
  }

  function selectHoursLine(cache, now) {
    if (!Number.isInteger(now) || cache?.version !== 1 || cache.source !== 'google-business-profile' || cache.timezone !== 'America/Toronto') return null;
    if (!Number.isInteger(cache.fetched_at) || !Number.isInteger(cache.expires_at) ||
        cache.fetched_at <= 0 || cache.fetched_at > now || cache.expires_at <= now ||
        cache.expires_at > cache.fetched_at + 14400 || !Array.isArray(cache.days)) return null;
    const days = cache.days.filter(day => Number.isInteger(day?.start_at) && Number.isInteger(day?.end_at) &&
      day.start_at <= now && day.end_at > now);
    if (days.length !== 1) return null;
    const line = days[0].line;
    return typeof line === 'string' && line.length <= 200 &&
      (line === 'Closed today.' || (line.startsWith('Open today ') && line.trim() === line)) ? line : null;
  }

  function applyHoursGreeting(root, now) {
    const greeting = root.querySelector(GREETING_SELECTOR);
    const defaultCopy = greeting?.querySelector('[data-pippa-default]');
    const hoursCopy = greeting?.querySelector('[data-pippa-hours-copy]');
    const hoursLine = greeting?.querySelector('[data-pippa-hours-line]');
    if (!greeting || !defaultCopy || !hoursCopy || !hoursLine) return null;
    let cache;
    try { cache = JSON.parse(greeting.getAttribute('data-pippa-hours') || 'null'); }
    catch { cache = null; }
    const line = selectHoursLine(cache, now);
    defaultCopy.hidden = Boolean(line);
    hoursCopy.hidden = !line;
    hoursLine.textContent = line || '';
    greeting.setAttribute('aria-label', line
      ? `Honk! ${line} Ask me anything.`
      : 'Honk! Let me know if you have any questions.');
    return line;
  }

  function createGreetingController(root, timing) {
    const greeting = root.querySelector(GREETING_SELECTOR);
    let scheduledTimer = null;
    let hideTimer = null;
    let concealTimer = null;
    let showFrame = null;

    const clear = (timerId) => {
      if (timerId !== null) timing.clearTimeout(timerId);
    };

    const hide = (immediate = false) => {
      clear(hideTimer);
      clear(concealTimer);
      hideTimer = null;
      concealTimer = null;
      if (showFrame !== null) {
        timing.cancelAnimationFrame(showFrame);
        showFrame = null;
      }

      if (!greeting) return;
      greeting.classList.remove('is-visible');

      if (immediate) {
        greeting.hidden = true;
        return;
      }

      concealTimer = timing.setTimeout(() => {
        concealTimer = null;
        greeting.hidden = true;
      }, GREETING_TRANSITION_MS);
    };

    const show = () => {
      if (!greeting) return;

      applyHoursGreeting(root, Math.floor(Date.now() / 1000));
      clear(hideTimer);
      clear(concealTimer);
      greeting.hidden = false;
      showFrame = timing.requestAnimationFrame(() => {
        showFrame = null;
        greeting.classList.add('is-visible');
      });

      hideTimer = timing.setTimeout(() => {
        hideTimer = null;
        hide(false);
      }, GREETING_DURATION_MS);
    };

    const schedule = (canShow) => {
      clear(scheduledTimer);
      scheduledTimer = timing.setTimeout(() => {
        scheduledTimer = null;
        if (canShow()) show();
      }, GREETING_DELAY_MS);
    };

    const destroy = () => {
      clear(scheduledTimer);
      clear(hideTimer);
      clear(concealTimer);
      if (showFrame !== null) timing.cancelAnimationFrame(showFrame);
    };

    return { destroy, hide, schedule, show };
  }

  function createPetController(root, overrides = {}) {
    const documentRoot = overrides.document || root.ownerDocument || document;
    const view = overrides.window || documentRoot.defaultView || window;
    const MutationObserverClass = overrides.MutationObserver || view.MutationObserver;
    const ResizeObserverClass = overrides.ResizeObserver === undefined ? view.ResizeObserver : overrides.ResizeObserver;
    const timing = {
      setTimeout: overrides.setTimeout || view.setTimeout.bind(view),
      clearTimeout: overrides.clearTimeout || view.clearTimeout.bind(view),
      requestAnimationFrame: overrides.requestAnimationFrame || view.requestAnimationFrame.bind(view),
      cancelAnimationFrame: overrides.cancelAnimationFrame || view.cancelAnimationFrame.bind(view),
    };
    const launcher = root.querySelector(LAUNCHER_SELECTOR);
    const greeting = createGreetingController(root, timing);
    let sessionStorage = overrides.sessionStorage;
    if (sessionStorage === undefined) {
      try {
        sessionStorage = view.sessionStorage;
      } catch (_error) {
        sessionStorage = null;
      }
    }
    let started = false;
    let chat = null;
    let activator = null;
    let observedShadowRoot = null;
    let observedStickyBar = null;
    let documentObserver = null;
    let chatObserver = null;
    let shadowObserver = null;
    let stickyBarObserver = null;
    let activatorObserver = null;
    let resizeObserver = null;
    let discoveryTimer = null;
    let stabilizationTimers = [];
    let stickyBarSettleTimers = [];
    let hasValidPosition = false;
    let greetingScheduled = false;

    const removeWindowListener = (target, eventName, handler) => {
      target?.removeEventListener?.(eventName, handler);
    };

    const clearDiscovery = () => {
      if (discoveryTimer === null) return;
      timing.clearTimeout(discoveryTimer);
      discoveryTimer = null;
    };

    const clearStabilization = () => {
      stabilizationTimers.forEach((timerId) => timing.clearTimeout(timerId));
      stabilizationTimers = [];
    };

    const clearStickyBarSettle = () => {
      stickyBarSettleTimers.forEach((timerId) => timing.clearTimeout(timerId));
      stickyBarSettleTimers = [];
    };

    const disconnectActivator = () => {
      activatorObserver?.disconnect();
      resizeObserver?.disconnect();
      activatorObserver = null;
      resizeObserver = null;
      activator = null;
      syncUnread(root, null);
    };

    const disconnectChat = () => {
      chatObserver?.disconnect();
      shadowObserver?.disconnect();
      chatObserver = null;
      shadowObserver = null;
      observedShadowRoot = null;
      disconnectActivator();
      clearDiscovery();
      clearStabilization();
    };

    let reposition;

    const handleStickyBarChange = () => {
      reposition();
      clearStickyBarSettle();
      stickyBarSettleTimers = getStickyBarSettleDelays(observedStickyBar, view).map((delay) =>
        timing.setTimeout(reposition, delay)
      );
    };

    const bindActivator = (nextActivator) => {
      if (nextActivator === activator) return activator;

      disconnectActivator();
      if (!nextActivator) return null;
      activator = nextActivator;
      activator.setAttribute('aria-hidden', 'true');
      activator.setAttribute('tabindex', '-1');

      if (ResizeObserverClass) {
        resizeObserver = new ResizeObserverClass(reposition);
        resizeObserver.observe(activator);
      }

      activatorObserver = new MutationObserverClass(() => syncUnread(root, activator));
      activatorObserver.observe(activator, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      syncUnread(root, activator);
      return activator;
    };

    const observeShadowRoot = () => {
      const nextShadowRoot = chat?.shadowRoot || null;
      if (nextShadowRoot === observedShadowRoot) return;

      shadowObserver?.disconnect();
      shadowObserver = null;
      observedShadowRoot = nextShadowRoot;

      if (!observedShadowRoot) return;
      shadowObserver = new MutationObserverClass(() => reposition());
      shadowObserver.observe(observedShadowRoot, { subtree: true, childList: true });
    };

    const observeStickyBar = () => {
      if (observedStickyBar && observedStickyBar.isConnected) return false;
      const nextBar = documentRoot.querySelector(STICKY_BAR_SELECTOR);
      if (nextBar === observedStickyBar) return false;

      stickyBarObserver?.disconnect();
      stickyBarObserver = null;
      observedStickyBar = nextBar;

      if (!observedStickyBar) return true;
      stickyBarObserver = new MutationObserverClass(handleStickyBarChange);
      stickyBarObserver.observe(observedStickyBar, {
        attributes: true,
        attributeFilter: ['hidden', 'class', 'style'],
      });
      return true;
    };

    const scheduleDiscovery = () => {
      if (discoveryTimer !== null || !chat) return;
      discoveryTimer = timing.setTimeout(() => {
        discoveryTimer = null;
        syncChat();
        if (chat && !activator) scheduleDiscovery();
      }, ACTIVATOR_DISCOVERY_MS);
    };

    const scheduleGreetingOnce = () => {
      if (greetingScheduled) return;
      greetingScheduled = true;
      greeting.schedule(
        () => Boolean(chat && !chat.hasAttribute('open') && hasValidPosition) && claimGreeting(sessionStorage)
      );
    };

    const positionNow = () => {
      if (!chat) return false;
      observeShadowRoot();
      if (observeStickyBar()) handleStickyBarChange();

      const nextActivator = getActivator(chat);
      if (nextActivator !== activator) bindActivator(nextActivator);

      if (!activator || !positionPet(root, activator, documentRoot)) {
        if (!hasValidPosition) root.classList.remove('is-ready');
        scheduleDiscovery();
        return false;
      }

      hasValidPosition = true;
      root.classList.add('is-ready');
      clearDiscovery();
      scheduleGreetingOnce();
      return true;
    };

    reposition = createFrameScheduler(
      positionNow,
      timing.requestAnimationFrame,
      timing.cancelAnimationFrame
    );

    const syncOpen = () => {
      const isOpen = Boolean(chat && chat.hasAttribute('open'));
      root.classList.toggle('is-chat-open', isOpen);
      if (isOpen) {
        setReading(root, false);
        greeting.hide(true);
      }
    };

    const stabilizePosition = () => {
      clearStabilization();
      stabilizationTimers = STABILIZATION_DELAYS_MS.map((delay) =>
        timing.setTimeout(reposition, delay)
      );
    };

    const bindChat = (nextChat) => {
      if (nextChat === chat) return;

      disconnectChat();
      chat = nextChat;
      hasValidPosition = false;
      root.classList.remove('is-ready');

      if (!chat) {
        syncOpen();
        return;
      }

      chatObserver = new MutationObserverClass(() => {
        syncOpen();
        stabilizePosition();
      });
      chatObserver.observe(chat, { attributes: true, attributeFilter: ['open', 'style'] });
      observeShadowRoot();
      syncOpen();
      stabilizePosition();
      positionNow();
    };

    function syncChat() {
      const currentChat = documentRoot.querySelector(CHAT_SELECTOR);
      if (currentChat !== chat) {
        bindChat(currentChat);
        return;
      }

      observeShadowRoot();
      positionNow();
      syncOpen();
    }

    const handlePointerEnter = () => setReading(root, true);
    const handlePointerLeave = () => setReading(root, false);
    const handleFocus = () => {
      root.classList.add('is-focused');
      setReading(root, true);
    };
    const handleBlur = () => {
      root.classList.remove('is-focused');
      setReading(root, false);
    };
    const handleClick = () => {
      syncChat();
      setReading(root, false);
      greeting.hide(true);
      openChat(chat);
      syncOpen();
    };

    const start = () => {
      if (started || !launcher) return false;
      started = true;

      launcher.addEventListener('pointerenter', handlePointerEnter);
      launcher.addEventListener('pointerleave', handlePointerLeave);
      launcher.addEventListener('focus', handleFocus);
      launcher.addEventListener('blur', handleBlur);
      launcher.addEventListener('click', handleClick);
      view.addEventListener('resize', reposition, { passive: true });
      view.addEventListener('scroll', reposition, { passive: true });
      view.visualViewport?.addEventListener('resize', reposition, { passive: true });
      view.visualViewport?.addEventListener('scroll', reposition, { passive: true });

      documentObserver = new MutationObserverClass(() => {
        const currentChat = documentRoot.querySelector(CHAT_SELECTOR);
        if (currentChat !== chat) {
          syncChat();
          return;
        }

        if (observeStickyBar()) handleStickyBarChange();
      });
      documentObserver.observe(documentRoot.documentElement || documentRoot, {
        subtree: true,
        childList: true,
      });

      syncChat();
      return true;
    };

    const destroy = () => {
      if (!started) return;
      started = false;
      documentObserver?.disconnect();
      documentObserver = null;
      stickyBarObserver?.disconnect();
      stickyBarObserver = null;
      observedStickyBar = null;
      clearStickyBarSettle();
      disconnectChat();
      reposition.cancel();
      greeting.destroy();

      launcher.removeEventListener('pointerenter', handlePointerEnter);
      launcher.removeEventListener('pointerleave', handlePointerLeave);
      launcher.removeEventListener('focus', handleFocus);
      launcher.removeEventListener('blur', handleBlur);
      launcher.removeEventListener('click', handleClick);
      removeWindowListener(view, 'resize', reposition);
      removeWindowListener(view, 'scroll', reposition);
      removeWindowListener(view.visualViewport, 'resize', reposition);
      removeWindowListener(view.visualViewport, 'scroll', reposition);
    };

    return {
      destroy,
      refresh: syncChat,
      start,
    };
  }

  function initialize() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || root.swanChatPetController) return;

    root.swanChatPetController = createPetController(root);
    root.swanChatPetController.start();
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      calculatePetPosition,
      claimGreeting,
      selectHoursLine,
      applyHoursGreeting,
      createGreetingController,
      createPetController,
      getStickyBarSettleDelays,
      openChat,
      parseDurationMs,
    };
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }
  }
})();
